import { useCallback, useEffect, useState } from 'react';

/**
 * Page width preference (2026-08-28).
 *
 * Michael: "on a lot of sections we have wasted space... like we are limiting
 * width but we have 4k monitors." Every list, table and dashboard capped at
 * 1536px — fine on a laptop, a third of the screen on a 3840-wide panel.
 *
 * Four settings. `auto` is the default and picks by viewport, so a 4K desk
 * monitor goes full-bleed with nothing to configure and a laptop stays
 * exactly as it was. The other three are the explicit overrides.
 *
 * Stored per BROWSER, not on the server, on purpose: the same person has a
 * 4K monitor at the desk and a 14" laptop in the truck, and one answer for
 * both would be wrong on one of them. Every storage read is wrapped —
 * localStorage throws outright in some contexts, and a page that cannot
 * lay itself out is far worse than a forgotten preference.
 *
 * Only the `full` and `wide` container tokens respond. `reading` (forms and
 * detail pages) keeps its 896px on every setting: that is a line-length
 * choice, and prose stretched to 3800px is genuinely harder to read.
 */
export type PageWidthPref = 'auto' | 'comfortable' | 'wide' | 'fluid';

/** What the layout actually applies — `auto` has been resolved away. */
export type ResolvedPageWidth = Exclude<PageWidthPref, 'auto'>;

export const PAGE_WIDTH_KEY = 'demoops.pageWidth.v1';

const PREFS: PageWidthPref[] = ['auto', 'comfortable', 'wide', 'fluid'];

export const PAGE_WIDTH_LABEL: Record<PageWidthPref, string> = {
  auto: 'Auto — fit the screen',
  comfortable: 'Comfortable (1536px)',
  wide: 'Wide (1880px)',
  fluid: 'Full width',
};

export function readPageWidthPref(): PageWidthPref {
  try {
    const raw = localStorage.getItem(PAGE_WIDTH_KEY);
    return PREFS.includes(raw as PageWidthPref) ? (raw as PageWidthPref) : 'auto';
  } catch {
    return 'auto';
  }
}

function writePageWidthPref(pref: PageWidthPref): void {
  try {
    localStorage.setItem(PAGE_WIDTH_KEY, pref);
  } catch {
    // Nothing to tell the user — the page still lays out, just not remembered.
  }
}

/**
 * `auto` by viewport. The thresholds are where each cap starts wasting real
 * space: at 1920 a 1536 cap leaves 384px of nothing, at 2560 even 1880 does.
 */
export function resolvePageWidth(pref: PageWidthPref, viewportWidth: number): ResolvedPageWidth {
  if (pref !== 'auto') return pref;
  if (viewportWidth >= 2560) return 'fluid';
  if (viewportWidth >= 1920) return 'wide';
  return 'comfortable';
}

/** The DOM stamp the stylesheet keys on — see index.css [data-page-width]. */
export function applyPageWidth(resolved: ResolvedPageWidth): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.pageWidth = resolved;
}

const CHANGE_EVENT = 'demoops:page-width';

/**
 * Read + write the preference, and keep every subscriber in step. A change
 * from the Settings page has to reach the layout that stamps the document
 * without a shared store, so the write fires a window event.
 */
export function usePageWidthPref(): [PageWidthPref, (next: PageWidthPref) => void] {
  const [pref, setPref] = useState<PageWidthPref>(() => readPageWidthPref());

  useEffect(() => {
    const sync = () => setPref(readPageWidthPref());
    window.addEventListener(CHANGE_EVENT, sync);
    return () => window.removeEventListener(CHANGE_EVENT, sync);
  }, []);

  const update = useCallback((next: PageWidthPref) => {
    writePageWidthPref(next);
    setPref(next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }, []);

  return [pref, update];
}

/**
 * Mount once in the app shell: resolves the preference against the live
 * viewport and stamps the document, re-resolving on resize so `auto` follows
 * a window dragged between monitors.
 */
export function usePageWidthStamp(): ResolvedPageWidth {
  const [pref] = usePageWidthPref();
  const [resolved, setResolved] = useState<ResolvedPageWidth>(() =>
    resolvePageWidth(pref, typeof window === 'undefined' ? 1440 : window.innerWidth),
  );

  useEffect(() => {
    const apply = () => {
      const next = resolvePageWidth(pref, window.innerWidth);
      setResolved(next);
      applyPageWidth(next);
    };
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [pref]);

  return resolved;
}
